import { extractCleanExamId } from "@shared/utils/eegExamId";

export type RecordingStatusVariant =
  | "success"
  | "warning"
  | "danger"
  | "secondary";

export interface RecordingTableModelInput {
  recordingId: string;
  filename?: string | null;
  recordingIdFromEdf?: string | null;
  derivationType?: string | null;
  derivationMetadata?: Record<string, unknown> | null;
  mergedSourceCount?: number | null;
  processingStatus?: string | null;
  numChannels?: number | null;
  durationSeconds?: number | null;
  logicalDurationSeconds?: number | null;
  startDateTime?: string | null;
  segmentIndex?: number;
  segmentTotal?: number;
  startDateTimeFormat?: "compact" | "full";
}

export interface RecordingTableModel {
  recordingId: string;
  examId: string;
  statusCode: string;
  statusLabel: string;
  statusVariant: RecordingStatusVariant;
  isPlayable: boolean;
  isMerged: boolean;
  mergedSourceCount: number;
  segmentLabel: string;
  channelsLabel: string;
  durationCompactLabel: string;
  durationDetailedLabel: string;
  metricsLabel: string;
  startDateTimeLabel: string;
}

function toValidDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDurationCompact(seconds: number): string {
  if (seconds <= 0) return "Duration unknown";
  return `${Math.round(seconds / 60)} min`;
}

export function formatDurationDetailed(seconds: number): string {
  if (seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}

function normalizeStatus(status: string | null | undefined): string {
  return String(status || "").trim().toLowerCase();
}

function resolveStatusVariant(statusCode: string): RecordingStatusVariant {
  if (statusCode === "complete" || statusCode === "completed") return "success";
  if (
    statusCode === "processing" ||
    statusCode === "preprocessing" ||
    statusCode === "queued" ||
    statusCode === "pending" ||
    statusCode === "running"
  ) {
    return "warning";
  }
  if (statusCode === "failed" || statusCode === "error") return "danger";
  return "secondary";
}

function resolveMergedSourceCount(input: RecordingTableModelInput): number {
  const directCount = Number(input.mergedSourceCount || 0);
  if (Number.isFinite(directCount) && directCount > 0) return directCount;
  const metadataCount = Number(
    (input.derivationMetadata as { source_count?: unknown } | null)?.source_count ||
      0,
  );
  return Number.isFinite(metadataCount) && metadataCount > 0 ? metadataCount : 0;
}

function formatStartDateTime(
  value: string | null | undefined,
  mode: "compact" | "full",
): string {
  const parsed = toValidDate(value);
  if (!parsed) return "Unknown start";
  if (mode === "compact") {
    return parsed.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return parsed.toLocaleString();
}

export function resolveRecordingExamId(input: {
  recordingIdFromEdf?: string | null;
  filename?: string | null;
}): string {
  return extractCleanExamId(input.recordingIdFromEdf, input.filename);
}

export function truncateLabelWithEllipsis(
  value: string,
  maxChars: number,
): { text: string; truncated: boolean } {
  const normalized = String(value || "");
  if (!normalized || maxChars < 1) {
    return { text: normalized, truncated: false };
  }
  if (normalized.length <= maxChars) {
    return { text: normalized, truncated: false };
  }
  return {
    text: `${normalized.slice(0, maxChars)}...`,
    truncated: true,
  };
}

export function buildRecordingTableModel(
  input: RecordingTableModelInput,
): RecordingTableModel {
  const statusCode = normalizeStatus(input.processingStatus);
  const statusLabel = statusCode || "unknown";
  const isMerged = String(input.derivationType || "").trim().toLowerCase() === "segment_merge";
  const mergedSourceCount = resolveMergedSourceCount(input);
  const durationSeconds = Number(
    input.logicalDurationSeconds || input.durationSeconds || 0,
  );
  const durationCompactLabel = formatDurationCompact(durationSeconds);
  const durationDetailedLabel = formatDurationDetailed(durationSeconds);
  const channelsLabel =
    Number.isFinite(Number(input.numChannels)) && Number(input.numChannels) > 0
      ? `${Number(input.numChannels)}`
      : "—";
  const segmentLabel = isMerged
    ? "Merged"
    : input.segmentIndex && input.segmentTotal
      ? `Segment ${input.segmentIndex}/${input.segmentTotal}`
      : "—";

  return {
    recordingId: input.recordingId,
    examId: resolveRecordingExamId({
      recordingIdFromEdf: input.recordingIdFromEdf,
      filename: input.filename,
    }),
    statusCode,
    statusLabel,
    statusVariant: resolveStatusVariant(statusCode),
    isPlayable: statusCode === "complete" || statusCode === "completed",
    isMerged,
    mergedSourceCount,
    segmentLabel,
    channelsLabel,
    durationCompactLabel,
    durationDetailedLabel,
    metricsLabel: `${channelsLabel} channels • ${durationCompactLabel}`,
    startDateTimeLabel: formatStartDateTime(
      input.startDateTime,
      input.startDateTimeFormat || "compact",
    ),
  };
}
